import React, { useEffect, useId, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import { API_BASE_URL } from "../config/serverApiConfig";
import { getRoleHome, normalizeRole } from "./Auth/PermissionType";
import { TEST_FIELDS, checksFromApi } from "./labTests";
import "./Report.css";

const TEAL = "#006d77";
const GOLD = "#c49a3c";
const CORAL = "#d96c5f";
const MIST = "#8aa8ad";
const SHORT = ["ک٢", "شوب", "ئاز", "نیس", "ئای", "حوز", "تەم", "ئاب", "ئەیل", "ت١", "ت٢", "ک١"];
const DAYS = [
  { label: "یەک", full: "یەکشەممە", mark: true },
  { label: "دوو", full: "دووشەممە", mark: true },
  { label: "سێ", full: "سێشەممە", mark: false },
  { label: "چوار", full: "چوارشەممە", mark: true },
  { label: "پێنج", full: "پێنجشەممە", mark: false },
  { label: "هەینی", full: "هەینی", mark: false },
  { label: "شەم", full: "شەممە", mark: false },
];

function formatCount(value) {
  return new Intl.NumberFormat("en-US").format(value || 0);
}

function parseDay(value) {
  if (!value) return null;
  const text = String(value).trim().slice(0, 10).replaceAll("/", "-");
  const parsed = dayjs(text);
  return parsed.isValid() ? parsed : null;
}

function monthKey(value) {
  const parsed = parseDay(value);
  return parsed ? parsed.format("YYYY-MM") : "";
}

function monthKeys(span) {
  const end = dayjs().startOf("month");
  const months = [];
  for (let i = span - 1; i >= 0; i -= 1) {
    const month = end.subtract(i, "month");
    months.push({
      key: month.format("YYYY-MM"),
      label: SHORT[month.month()],
      full: `${SHORT[month.month()]} ${month.year()}`,
    });
  }
  return months;
}

function inside(date, start, end) {
  return date && !date.isBefore(start, "day") && !date.isAfter(end, "day");
}

async function loadPatients() {
  const size = 400;
  const first = await axios.get(`${API_BASE_URL}patient`, { params: { page: 0, size, sort: "DESC" } });
  const total = Math.min(first.data.totalPages || 1, 15);
  let rows = first.data.content || [];
  if (total > 1) {
    const rest = await Promise.all(
      Array.from({ length: total - 1 }, (_, index) =>
        axios.get(`${API_BASE_URL}patient`, { params: { page: index + 1, size, sort: "DESC" } })
      )
    );
    rest.forEach((page) => {
      rows = rows.concat(page.data.content || []);
    });
  }
  return rows;
}

function buildReport(raw, span) {
  const months = monthKeys(span);
  const start = dayjs(`${months[0].key}-01`).startOf("month");
  const end = dayjs().endOf("day");
  const index = Object.fromEntries(months.map((month, place) => [month.key, place]));
  const patients = Array(span).fill(0);
  const surgeries = Array(span).fill(0);
  const done = Array(span).fill(0);
  const brace = Array(span).fill(0);
  const mold = Array(span).fill(0);
  const week = Array.from({ length: 7 }, () => [0, 0, 0]);
  const backlog = Array(span).fill(0);
  let older = 0;

  (raw.patients || []).forEach((item) => {
    const key = monthKey(item.date);
    if (key in index) patients[index[key]] += 1;
  });

  (raw.appointments || []).forEach((item) => {
    const surgery = parseDay(item.surgeryDate);
    const qalib = parseDay(item.qalibDate);
    const taqal = parseDay(item.taqalDate);
    if (inside(surgery, start, end)) {
      const key = surgery.format("YYYY-MM");
      if (key in index) {
        const place = index[key];
        surgeries[place] += 1;
        if (qalib && taqal) done[place] += 1;
        else if (qalib) brace[place] += 1;
        else mold[place] += 1;
      }
      week[surgery.day()][0] += 1;
    }
    if (inside(qalib, start, end)) week[qalib.day()][1] += 1;
    if (inside(taqal, start, end)) week[taqal.day()][2] += 1;
  });

  const tests = TEST_FIELDS.map((field) => ({ label: field.label, count: 0 }));
  (raw.labs || []).forEach((item) => {
    const checks = checksFromApi(item);
    TEST_FIELDS.forEach((field, place) => {
      if (checks[field.form]) tests[place].count += 1;
    });
    const date = parseDay(item.date);
    if (!date) return;
    const key = date.format("YYYY-MM");
    if (key in index) backlog[index[key]] += 1;
    else if (date.isBefore(start, "day")) older += 1;
  });

  return {
    months,
    patients,
    surgeries,
    done,
    brace,
    mold,
    week,
    backlog,
    older,
    queue: (raw.labs || []).length,
    tests: tests.filter((item) => item.count > 0).sort((a, b) => b.count - a.count).slice(0, 8),
    cards: raw.cards || { active: 0, reserve: 0, archive: 0 },
  };
}

function Legend({ items }) {
  return (
    <ul className="report-legend">
      {items.map((item) => (
        <li key={item.label}>
          <i style={{ background: item.color }} />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

function LineChart({ months, patients, surgeries }) {
  const reactId = useId().replace(/:/g, "");
  const [hover, setHover] = useState(null);
  const width = 640;
  const height = 220;
  const padL = 28;
  const padR = 8;
  const padT = 16;
  const padB = 12;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const max = Math.max(1, ...patients);
  const hasData = patients.some(Boolean) || surgeries.some(Boolean);
  const xAt = (place) => (
    months.length <= 1 ? padL + innerW / 2 : padL + (place / (months.length - 1)) * innerW
  );
  const yAt = (value) => padT + innerH - (value / max) * innerH;
  const points = (series) => series.map((value, place) => ({ x: xAt(place), y: yAt(value) }));
  const patientPoints = points(patients);
  const line = (series) => series.map((point, place) => `${place ? "L" : "M"}${point.x} ${point.y}`).join(" ");
  const last = patientPoints.length - 1;
  const area = patientPoints.length
    ? `${line(patientPoints)} L ${patientPoints[last].x} ${padT + innerH} L ${patientPoints[0].x} ${padT + innerH} Z`
    : "";
  const rawTip = hover == null ? 50 : (xAt(hover) / width) * 100;
  const tipAt = Math.min(84, Math.max(16, rawTip));

  return (
    <div className="chart-canvas">
      <div className="chart-plot">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="نەخۆشی نوێ و نەشتەرگەری بەپێی مانگ">
        <defs>
          <linearGradient id={reactId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={TEAL} stopOpacity="0.2" />
            <stop offset="100%" stopColor={TEAL} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.5, 1].map((step) => {
          const y = padT + innerH - step * innerH;
          return <line key={step} x1={padL} x2={width - padR} y1={y} y2={y} stroke="#e7f1f1" />;
        })}
        <text className="chart-num" x={padL - 6} y={padT + 4} textAnchor="end">{max}</text>
        <text className="chart-num" x={padL - 6} y={padT + innerH} textAnchor="end">0</text>
        {hasData && hover != null && (
          <line x1={xAt(hover)} x2={xAt(hover)} y1={padT} y2={padT + innerH} stroke="#d5e4e4" />
        )}
        {hasData && <path d={area} fill={`url(#${reactId})`} />}
        {hasData && (
          <path d={line(patientPoints)} fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {hasData && patientPoints.map((point, place) => (
          <circle key={`p-${months[place].key}`} cx={point.x} cy={point.y} r={place === last ? 4.5 : 3.2} fill="#fff" stroke={TEAL} strokeWidth="2" />
        ))}
      </svg>
      <div className="chart-hit" onMouseLeave={() => setHover(null)}>
        {months.map((month, place) => (
          <button
            key={month.key}
            type="button"
            className={hover === place ? "is-on" : ""}
            aria-label={month.full}
            onMouseEnter={() => setHover(place)}
            onFocus={() => setHover(place)}
            onBlur={() => setHover(null)}
          />
        ))}
      </div>
      {hasData && hover != null && (
        <div className="chart-tip" dir="rtl" style={{ left: `${tipAt}%` }}>
          <strong>{months[hover].full}</strong>
          <span><i style={{ background: TEAL }} />نەخۆش {formatCount(patients[hover])}</span>
          <span><i style={{ background: GOLD }} />نەشتەرگەری {formatCount(surgeries[hover])}</span>
        </div>
      )}
      </div>
      <div className="surg-row" aria-hidden="true">
        <span>{Math.max(...surgeries, 0) || ""}</span>
        <div>
          {surgeries.map((value, place) => (
            <span key={months[place].key}>
              {value > 0 && <i style={{ height: `${(value / Math.max(...surgeries, 1)) * 100}%` }} />}
            </span>
          ))}
        </div>
      </div>
      <div className="chart-axis">
        {months.map((month, place) => (
          <span key={month.key} className={place === months.length - 1 ? "is-now" : ""}>{month.label}</span>
        ))}
      </div>
      {!hasData && <p className="chart-empty">لەم ماوەیەدا جوڵە نییە</p>}
    </div>
  );
}

function StackChart({ rows, segments, week }) {
  const max = Math.max(1, ...rows.map((row) => row.values.reduce((sum, value) => sum + value, 0)));
  const empty = rows.every((row) => row.values.every((value) => value === 0));
  if (empty) return <p className="chart-empty">لەم ماوەیەدا تۆمار نییە</p>;
  return (
    <div className={week ? "stack-chart is-week" : "stack-chart"}>
      {rows.map((row) => (
        <button
          key={row.key}
          type="button"
          className="stack-col"
          onMouseEnter={row.onEnter}
          onFocus={row.onEnter}
          onMouseLeave={row.onLeave}
          onBlur={row.onLeave}
        >
          <span className="stack-total">{row.values.reduce((sum, value) => sum + value, 0) || ""}</span>
          <span className="stack-bar">
            {row.values.map((value, place) => (
              value > 0 ? <i key={segments[place].label} style={{ height: `${(value / max) * 100}%`, background: segments[place].color }} /> : null
            ))}
          </span>
          <span className={row.mark ? "stack-label is-mark" : "stack-label"}>{row.label}</span>
        </button>
      ))}
    </div>
  );
}

function Donut({ parts }) {
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  const radius = 58;
  const length = 2 * Math.PI * radius;
  const gaps = parts.filter((part) => part.value > 0).length > 1 ? 4 : 0;
  let offset = 0;
  return (
    <div className="donut-layout">
      <div className="donut-wrap">
        <svg viewBox="0 0 160 160" role="img" aria-label="دۆخی نەخۆش">
          <circle cx="80" cy="80" r={radius} fill="none" stroke="#eef4f4" strokeWidth="14" />
          <g transform="rotate(-90 80 80)">
            {parts.map((part) => {
              const slice = total ? Math.max(0, (part.value / total) * length - gaps) : 0;
              const dash = `${slice} ${length - slice}`;
              const node = (
                <circle
                  key={part.label}
                  cx="80"
                  cy="80"
                  r={radius}
                  fill="none"
                  stroke={part.color}
                  strokeWidth="14"
                  strokeDasharray={dash}
                  strokeDashoffset={-offset}
                />
              );
              offset += slice + (part.value > 0 ? gaps : 0);
              return node;
            })}
          </g>
        </svg>
        <div className="donut-center">
          <strong>{formatCount(total)}</strong>
          <span>نەخۆش</span>
        </div>
      </div>
      <ul className="donut-list">
        {parts.map((part) => (
          <li key={part.label}>
            <i style={{ width: 8, height: 8, borderRadius: 99, background: part.color }} />
            <span>{part.label}</span>
            <b>{formatCount(part.value)}</b>
            <span className="share"><i style={{ width: `${total ? (part.value / total) * 100 : 0}%`, background: part.color }} /></span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Report() {
  const auth = useSelector((state) => state.auth.auth);
  const [span, setSpan] = useState(6);
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partial, setPartial] = useState(false);
  const [pathNote, setPathNote] = useState("");
  const [dayNote, setDayNote] = useState("");

  useEffect(() => {
    if (normalizeRole(auth?.role) !== "admin") return undefined;
    let live = true;
    setLoading(true);
    Promise.allSettled([
      loadPatients(),
      axios.get(`${API_BASE_URL}appointment`),
      axios.get(`${API_BASE_URL}uncheck/res`),
      axios.get(`${API_BASE_URL}patient/cards`),
    ]).then((results) => {
      if (!live) return;
      const [patients, appointments, labs, cards] = results;
      setPartial(results.some((item) => item.status === "rejected"));
      setRaw({
        patients: patients.status === "fulfilled" ? patients.value : [],
        appointments: appointments.status === "fulfilled" ? appointments.value.data || [] : [],
        labs: labs.status === "fulfilled" ? labs.value.data || [] : [],
        cards: cards.status === "fulfilled" ? cards.value.data || {} : {},
      });
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [auth?.role]);

  const view = useMemo(() => (raw ? buildReport(raw, span) : null), [raw, span]);
  if (auth?.role && normalizeRole(auth.role) !== "admin") {
    return <Navigate to={getRoleHome(auth.role)} replace />;
  }

  const rangeNote = `${span} مانگی ڕابردوو`;
  const pathSegments = [
    { label: "تەواو", color: TEAL },
    { label: "تەقەڵ ماوە", color: GOLD },
    { label: "قالب ماوە", color: CORAL },
  ];
  const daySegments = [
    { label: "نەشتەرگەری", color: TEAL },
    { label: "قالب", color: GOLD },
    { label: "تەقەڵ", color: CORAL },
  ];
  const parts = view ? [
    { label: "چالاک", value: Number(view.cards.active) || 0, color: TEAL },
    { label: "جێگرەوە", value: Number(view.cards.reserve) || 0, color: GOLD },
    { label: "بلاک", value: Number(view.cards.archive) || 0, color: MIST },
  ] : [];
  const topTest = view?.tests[0]?.count || 1;

  return (
    <div className="slb2">
      <div className="report-page" dir="rtl">
        <header className="report-head">
          <div>
            <h1>ڕاپۆرت</h1>
            <p>جوڵەی کلینیک، ڕێڕەوی نەشتەرگەری، و پشکنینە چاوەڕوانەکان.</p>
          </div>
          <div className="report-range" role="group" aria-label="ماوە">
            {[6, 12].map((value) => (
              <button key={value} type="button" className={span === value ? "is-on" : ""} onClick={() => setSpan(value)}>
                {value} مانگ
              </button>
            ))}
          </div>
        </header>

        {loading || !view ? (
          <div className="report-kpis" aria-hidden="true">
            <div className="report-shimmer" />
            <div className="report-shimmer" />
            <div className="report-shimmer" />
            <div className="report-shimmer" />
          </div>
        ) : (
          <>
            <section className="report-kpis">
              <article className="report-kpi" style={{ "--spine": TEAL }}>
                <p className="kpi-label">نەخۆشی نوێ</p>
                <p className="kpi-value">{formatCount(view.patients.reduce((sum, value) => sum + value, 0))}</p>
                <p className="kpi-note">{rangeNote}</p>
              </article>
              <article className="report-kpi" style={{ "--spine": GOLD }}>
                <p className="kpi-label">نەشتەرگەری</p>
                <p className="kpi-value">{formatCount(view.surgeries.reduce((sum, value) => sum + value, 0))}</p>
                <p className="kpi-note">{rangeNote}</p>
              </article>
              <article className="report-kpi" style={{ "--spine": CORAL }}>
                <p className="kpi-label">چاوەڕوانی تاقیگە</p>
                <p className="kpi-value">{formatCount(view.queue)}</p>
                <p className="kpi-note">ئێستا، نەتاقیکراو</p>
              </article>
              <article className="report-kpi" style={{ "--spine": "#0e4045" }}>
                <p className="kpi-label">جێگرەوە</p>
                <p className="kpi-value">{formatCount(view.cards.reserve)}</p>
                <p className="kpi-note">ئێستا</p>
              </article>
            </section>

            <div className="report-grid">
              <section className="report-card">
                <header className="report-card-head">
                  <div>
                    <h2>جوڵەی کلینیک</h2>
                    <p>نەخۆشی نوێ و نەشتەرگەری بەپێی مانگ</p>
                  </div>
                  <Legend items={[{ label: "نەخۆش", color: TEAL }, { label: "نەشتەرگەری", color: GOLD }]} />
                </header>
                <LineChart months={view.months} patients={view.patients} surgeries={view.surgeries} />
              </section>
              <section className="report-card">
                <header className="report-card-head">
                  <div>
                    <h2>دۆخی نەخۆش</h2>
                    <p>ئێستا، بەبێ ماوە</p>
                  </div>
                </header>
                <Donut parts={parts} />
              </section>
            </div>

            <div className="report-grid is-wide">
              <section className="report-card">
                <header className="report-card-head">
                  <div>
                    <h2>ڕێڕەوی نەشتەرگەری</h2>
                    <p>{pathNote || "قالب دووشەممە و چوارشەممە · تەقەڵ یەکشەممە"}</p>
                  </div>
                  <Legend items={pathSegments} />
                </header>
                <StackChart
                  segments={pathSegments}
                  rows={view.months.map((month, place) => ({
                    key: month.key,
                    label: month.label,
                    mark: place === view.months.length - 1,
                    values: [view.done[place], view.brace[place], view.mold[place]],
                    onEnter: () => setPathNote(`${month.full} · تەواو ${view.done[place]} · تەقەڵ ماوە ${view.brace[place]} · قالب ماوە ${view.mold[place]}`),
                    onLeave: () => setPathNote(""),
                  }))}
                />
              </section>
            </div>

            <div className="report-grid">
              <section className="report-card">
                <header className="report-card-head">
                  <div>
                    <h2>ڕۆژانی کلینیک</h2>
                    <p>{dayNote || "یەکشەممە، دووشەممە و چوارشەممە ڕۆژی کارن"}</p>
                  </div>
                  <Legend items={daySegments} />
                </header>
                <StackChart
                  week
                  segments={daySegments}
                  rows={DAYS.map((day, place) => ({
                    key: day.full,
                    label: day.label,
                    mark: day.mark,
                    values: view.week[place],
                    onEnter: () => setDayNote(`${day.full} · نەشتەرگەری ${view.week[place][0]} · قالب ${view.week[place][1]} · تەقەڵ ${view.week[place][2]}`),
                    onLeave: () => setDayNote(""),
                  }))}
                />
              </section>
              <section className="report-card">
                <header className="report-card-head">
                  <div>
                    <h2>پشکنینە چاوەڕوانەکان</h2>
                    <p>
                      {view.older
                        ? `${formatCount(view.older)} داواکاری کۆنتر لەم ماوەیە هێشتا ماون`
                        : "تەنها ئەوانەی هێشتا نەتاقیکراون"}
                    </p>
                  </div>
                </header>
                {view.tests.length === 0 ? (
                  <p className="chart-empty">پشکنینی چاوەڕوان نییە</p>
                ) : (
                  <div className="lab-list">
                    {view.tests.map((item) => (
                      <div className="lab-row" key={item.label}>
                        <span>{item.label}</span>
                        <span className="lab-track"><i style={{ width: `${(item.count / topTest) * 100}%`, opacity: 0.45 + (item.count / topTest) * 0.55 }} /></span>
                        <b>{formatCount(item.count)}</b>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
            {partial && <p className="report-status">بەشێک لە داتا نەهات. ئەوەی ماوەتەوە لە خوارەوەیە.</p>}
          </>
        )}
      </div>
    </div>
  );
}

export default Report;
